import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { AppShell } from './components/AppShell';
import { Dashboard } from './components/Dashboard';
import { UsersView } from './components/users/UsersView';
import { UserDetailView } from './components/users/UserDetailView';
import { ProjectsView } from './components/projects/ProjectsView';
import { ProjectDetailView } from './components/projects/ProjectDetailView';
import { ModelsView } from './components/models/ModelsView';
import { IpAllowlistView } from './components/ip/IpAllowlistView';
import { UsageDashboard } from './components/usage/UsageDashboard';
import { ProfileView } from './components/ProfileView';
import { MetricsView } from './components/MetricsView';
import { SetupView } from './components/SetupView';
import { RequestsView } from './components/RequestsView';
import { RequestAccessView } from './components/RequestAccessView';
import { TeamsView } from './components/teams/TeamsView';
import { TeamDetailView } from './components/teams/TeamDetailView';
import { AuditLogView } from './components/AuditLogView';
import { CacheView } from './components/CacheView';
import { StatusView } from './components/StatusView';
import { ReportsView } from './components/ReportsView';
import { AccountsView } from './components/accounts/AccountsView';
import { AccountDetailView } from './components/accounts/AccountDetailView';

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading, isSigningOut, signIn } = useAuth();

  // Track sign-in attempts across redirects using sessionStorage with a timestamp.
  // The flag expires after 2 minutes so approved users can retry without clearing state.
  const SIGN_IN_KEY = 'flux_sign_in_attempted';
  const SIGN_IN_EXPIRY = 2 * 60 * 1000; // 2 minutes

  const getSignInFlag = (): boolean => {
    const raw = sessionStorage.getItem(SIGN_IN_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (isNaN(ts)) return false;
    // Expired?
    if (Date.now() - ts > SIGN_IN_EXPIRY) {
      sessionStorage.removeItem(SIGN_IN_KEY);
      return false;
    }
    return true;
  };

  const isAuthDenied = !isLoading && !isAuthenticated && getSignInFlag();

  // Clear the flag when successfully authenticated
  useEffect(() => {
    if (isAuthenticated) {
      sessionStorage.removeItem(SIGN_IN_KEY);
    }
  }, [isAuthenticated]);

  if (isAuthDenied) {
    return <AccessDeniedPage onRetry={() => { sessionStorage.removeItem(SIGN_IN_KEY); signIn(); }} />;
  }

  if (isLoading) {
    return (
      <div className="auth-loading">
        <img src="/admin/flux-logo-full.png" alt="Flux" className="auth-loading__logo-img" />
        <p>Loading...</p>
      </div>
    );
  }

  if (isSigningOut) {
    return (
      <div className="auth-loading">
        <img src="/admin/flux-logo-full.png" alt="Flux" className="auth-loading__logo-img" />
        <p>Signing out...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    // Mark that we're attempting sign-in with timestamp (persists across redirect, expires after 2 min)
    sessionStorage.setItem(SIGN_IN_KEY, String(Date.now()));
    signIn();
    return (
      <div className="auth-loading">
        <img src="/admin/flux-logo-full.png" alt="Flux" className="auth-loading__logo-img" />
        <p>Redirecting to sign in...</p>
      </div>
    );
  }

  return <>{children}</>;
}

function AccessDeniedPage({ onRetry }: { onRetry: () => void }) {
  const handleSignOut = async () => {
    // Clear all local auth state and redirect to Cognito logout
    sessionStorage.removeItem('flux_sign_in_attempted');
    // Clear Amplify's cached tokens
    try {
      const { signOut } = await import('aws-amplify/auth');
      await signOut();
    } catch {
      // If Amplify signOut fails (no tokens), just clear storage manually
    }
    // Clear any remaining auth data
    localStorage.clear();
    sessionStorage.clear();
    // Redirect to Cognito logout endpoint which clears the Cognito session cookie
    const domain = import.meta.env.VITE_COGNITO_DOMAIN?.replace(/^https?:\/\//, '') ?? '';
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID ?? '';
    const redirectUri = encodeURIComponent(window.location.origin + '/admin/');
    if (domain && clientId) {
      window.location.href = `https://${domain}/logout?client_id=${clientId}&logout_uri=${redirectUri}`;
    } else {
      window.location.reload();
    }
  };
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const API_BASE = import.meta.env.VITE_API_URL ?? '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !name) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/mgmt/request-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, reason }),
      });
      const data = await res.json();
      if (res.ok || res.status === 409) {
        setSubmitted(true);
      } else {
        setError(data.error || 'Failed to submit request');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="auth-loading">
        <img src="/admin/flux-logo-full.png" alt="Flux" className="auth-loading__logo-img" />
        <h2 style={{ color: '#00c853', marginBottom: 12 }}>Request Submitted</h2>
        <p style={{ color: '#8b95a8', maxWidth: 400, textAlign: 'center', lineHeight: 1.6 }}>
          Your access request has been submitted. An administrator will review it and
          you'll be able to log in once approved.
        </p>
        <button onClick={onRetry} className="btn btn--secondary" style={{ marginTop: 20 }}>
          Try Logging In Again
        </button>
        <button onClick={handleSignOut} className="btn btn--secondary" style={{ marginTop: 8, opacity: 0.7 }}>
          Sign Out / Switch Account
        </button>
      </div>
    );
  }

  return (
    <div className="auth-loading">
      <img src="/admin/flux-logo-full.png" alt="Flux" className="auth-loading__logo-img" />
      <h2 style={{ color: '#ffab00', marginBottom: 8 }}>Access Required</h2>
      <p style={{ color: '#8b95a8', maxWidth: 420, textAlign: 'center', lineHeight: 1.6, marginBottom: 20 }}>
        Your account is not yet registered for Flux. Fill out the form below to request access.
      </p>
      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: 380 }}>
        {error && (
          <div style={{ background: 'rgba(255,23,68,0.08)', border: '1px solid rgba(255,23,68,0.2)', borderRadius: 6, padding: '8px 12px', color: '#ff1744', fontSize: 13, marginBottom: 12 }}>
            {error}
          </div>
        )}
        <div style={{ marginBottom: 12 }}>
          <input
            type="email"
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            style={{ width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid #1e2d4a', background: '#0a1225', color: '#e8edf5', fontSize: 14 }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            style={{ width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid #1e2d4a', background: '#0a1225', color: '#e8edf5', fontSize: 14 }}
          />
        </div>
        <div style={{ marginBottom: 16 }}>
          <textarea
            placeholder="Why do you need access? (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            style={{ width: '100%', padding: '10px 14px', borderRadius: 6, border: '1px solid #1e2d4a', background: '#0a1225', color: '#e8edf5', fontSize: 14, resize: 'vertical' }}
          />
        </div>
        <button
          type="submit"
          disabled={submitting || !email || !name}
          className="btn btn--primary"
          style={{ width: '100%', marginBottom: 10 }}
        >
          {submitting ? 'Submitting...' : 'Request Access'}
        </button>
        <button type="button" onClick={onRetry} className="btn btn--secondary" style={{ width: '100%' }}>
          Already Approved? Try Again
        </button>
        <button type="button" onClick={handleSignOut} style={{ width: '100%', marginTop: 8, background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
          Sign out / switch account
        </button>
      </form>
    </div>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RequireAuth>
          <Routes>
            {/* App shell wraps all admin routes */}
            <Route element={<AppShell />}>
              <Route path="/admin" element={<Dashboard />} />
              <Route path="/admin/setup" element={<SetupView />} />
              <Route path="/admin/users" element={<UsersView />} />
              <Route path="/admin/users/:username" element={<UserDetailView />} />
              <Route path="/admin/projects" element={<ProjectsView />} />
              <Route path="/admin/projects/:projectId" element={<ProjectDetailView />} />
              <Route path="/admin/models" element={<ModelsView />} />
              <Route path="/admin/accounts" element={<AccountsView />} />
              <Route path="/admin/accounts/:accountId" element={<AccountDetailView />} />
              <Route path="/admin/ip" element={<IpAllowlistView />} />
              <Route path="/admin/usage" element={<UsageDashboard />} />
              <Route path="/admin/metrics" element={<MetricsView />} />
              <Route path="/admin/me" element={<ProfileView />} />
              <Route path="/admin/teams" element={<TeamsView />} />
              <Route path="/admin/teams/:teamId" element={<TeamDetailView />} />
              <Route path="/admin/audit" element={<AuditLogView />} />
              <Route path="/admin/cache" element={<CacheView />} />
              <Route path="/admin/status" element={<StatusView />} />
              <Route path="/admin/reports" element={<ReportsView />} />
              <Route path="/admin/requests" element={<RequestsView />} />
              <Route path="/admin/request-access" element={<RequestAccessView />} />
            </Route>

            {/* Redirects */}
            <Route path="/" element={<Navigate to="/admin" replace />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </RequireAuth>
      </AuthProvider>
    </BrowserRouter>
  );
}
